import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isQuestion } from '@atlassian/search-ai/ai-answer-question-detection/question-detection';
import { QueryIntent } from '@atlassian/search-ai/constants';
import { hasSAINLayerParamValue } from '@atlassian/search-ai/get-sain-layer-frontend-experiment';
import { getQueryIntent } from '@atlassian/search-ai/getQueryIntent';
import { localStore as knowledgeDiscoveryLocalStore } from '@atlassian/search-ai/local-store';
import { getSmartAnswerRoute } from '@atlassian/search-ai/query-intent-api';
import {
	type DetectionMetadata,
	type IntentClassificationData,
	type QueryIntentResult,
} from '@atlassian/search-ai/query-intent-api/types';

import {
	onQueryIntentFailure,
	onQueryIntentFired,
	onQueryIntentReturned,
} from '../../common/utils/events/smart-answers-interacted';
import { useAnalytics, useCommonAndPrivateAttributes } from '../analytics';
import { useSmartAnswersIntentStore } from '../smart-answers-intent-store';
import { useBootstrap } from '../store/bootstrap';
import { useSearchQuery, useSelectedProduct } from '../store/search';
import { buildIntentDetectionVariables } from './build-intent-variables';

/**
 * Hook that runs the SAIN query-intent classification and exposes its
 * results as React state for SmartAnswers to consume.
 *
 * SIDE EFFECT: also writes the classified intent and the readiness flag
 * into the shared `smart-answers-intent-store` (via
 * `setQueryIntent` / `setIsIntentClassified`) so other components (e.g.
 * `AutoOpenRovoChatOnLanding`) can read SAIN's authoritative intent
 * without mounting a second classifier. This intentional duplication keeps
 * the public `useQueryIntent` API React-friendly while still feeding the
 * shared store as the cross-component source of truth.
 *
 * Should only be mounted by a single component (`SmartAnswers`) — mounting
 * it twice would fire duplicate classification API calls and analytics
 * events.
 */
export const useQueryIntent: () => {
	loading: boolean;
	transformedQuery: string;
	queryIntent: QueryIntent;
	queryIntentClassificationData: IntentClassificationData | undefined;
	queryIntentDetectionMetadata: DetectionMetadata | undefined;
	requestId: string | undefined;
	traceId: string | undefined;
	/**
	 * The query the intent state was classified for. For one render after the query changes, `loading`
	 * is still false and the intent still belongs to the previous query, so compare this with the current
	 * query before trusting the intent.
	 */
	classifiedQuery?: string;
} = () => {
	const intl = useIntl();
	const [searchQuery] = useSearchQuery();
	const [loading, setLoading] = useState(false);
	const [queryIntent, setQueryIntent] = useState(QueryIntent.DEFAULT);
	const [transformedQuery, setTransformedQuery] = useState('');
	const [queryIntentClassificationData, setQueryIntentClassificationData] = useState<
		IntentClassificationData | undefined
	>();
	const [queryIntentDetectionMetadata, setQueryIntentDetectionMetadata] = useState<
		DetectionMetadata | undefined
	>();
	const [requestId, setRequestId] = useState<string | undefined>();
	const [traceId, setTraceId] = useState<string | undefined>();
	const [classifiedQuery, setClassifiedQuery] = useState<string | undefined>();
	const [{ analyticsAttributes, cloudId, primaryProduct, orgId }] = useBootstrap();
	// Wire intent-classification readiness into the shared store so other components
	// (e.g. AutoOpenRovoChatOnLanding) can wait until SAIN's decision is in.
	const { setIsIntentClassified, setQueryIntent: setSharedQueryIntent } =
		useSmartAnswersIntentStore();
	const [selectedProduct] = useSelectedProduct();
	// Derivation lives in a shared helper so the QF warm-up site (which fires
	// the SAIN preload from outside React's render scope) produces a
	// byte-identical InMemCache key. Any drift here = wasted preload.
	const intentVariablesBase = useMemo(
		() =>
			buildIntentDetectionVariables({
				cloudId: cloudId ?? '',
				primaryProduct,
				activationId: analyticsAttributes?.activationId,
				locale: intl.locale,
				orgId,
				// `query` is overwritten per-call below — the helper just needs a
				// placeholder so the returned shape is complete.
				query: '',
			}),
		[cloudId, primaryProduct, analyticsAttributes?.activationId, intl.locale, orgId],
	);
	const { siteId: siteARI } = intentVariablesBase;
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const numberOfRecentDocuments = useMemo(() => {
		if (!fg('kd_recent_activity_intent') || !searchQuery) {
			return undefined;
		}
		return Number(knowledgeDiscoveryLocalStore.getString('recent-activity-count'));
	}, [searchQuery]);
	const metadata = !!numberOfRecentDocuments ? { numberOfRecentDocuments } : undefined;

	const isIntentDetectionV2OnHelloEnabled = fg('kd_intent_v2_exp_gate');
	const isIntentDetectionV2CustomerTestCohort =
		hasSAINLayerParamValue('intent_v2_cohort', 'test') && fg('kd_intent_v2_customer_exp_gate');

	const onQueryIntentFailed = useCallback(() => {
		fireAnalyticsEvent(
			onQueryIntentFailure({
				commonAttributes,
				nonPrivacySafeAttributes,
				cloudId,
			}),
		);

		if (isQuestion({ query: searchQuery, locale: intl.locale })) {
			setQueryIntent(QueryIntent.NATURAL_LANGUAGE_QUERY);
			setSharedQueryIntent(QueryIntent.NATURAL_LANGUAGE_QUERY);
		}
	}, [
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
		intl.locale,
		searchQuery,
		cloudId,
		setSharedQueryIntent,
	]);

	const onQueryIntentFire = useCallback(() => {
		fireAnalyticsEvent(
			onQueryIntentFired({
				commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
	}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes]);

	// Fires onQueryIntentReturned once the API layer has fully resolved the intent — including
	// its detection metadata — so the detection method is attributed to the right intent. Firing
	// from the intent setter instead would run before metadata is stored and risk misattribution.
	// Firing imperatively here (rather than from a useEffect on the intent state) also guarantees
	// one event per query even when consecutive queries resolve to the same intent.
	const onQueryIntentComplete = useCallback(
		({ queryIntent: intent, source, detectionMetadata }: QueryIntentResult) => {
			if (intent === QueryIntent.DEFAULT) {
				return;
			}
			fireAnalyticsEvent(
				onQueryIntentReturned({
					commonAttributes,
					nonPrivacySafeAttributes,
					cloudId,
					queryIntentClassification: intent,
					intentDetectionMethod: detectionMetadata?.method || 'UNKNOWN',
					intentDetectionSource: source,
					intentDetectionBackendCacheHit: detectionMetadata?.cacheHit,
				}),
			);
		},
		[fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes, cloudId],
	);

	useEffect(() => {
		if (!searchQuery) {
			return;
		}

		// Snapshot the query at call time so we ignore stale resolutions if
		// the query changes mid-flight (otherwise the older request could flip
		// `isIntentClassified` true while the newer query is still classifying).
		const requestQuery = searchQuery;
		const callGetSmartAnswerRoute = async () => {
			const intentVariables = {
				...intentVariablesBase,
				query: searchQuery,
			};
			// Reset shared store so consumers can't read stale values from the
			// previous classification while this one is in flight.
			setSharedQueryIntent(QueryIntent.DEFAULT);
			setIsIntentClassified(false);
			setLoading(true);
			onQueryIntentFire();
			// Always also write to the shared store so consumers using
			// useSharedQueryIntent() see the same value SAIN does.
			const setLocalAndShared = (intent: QueryIntent) => {
				setQueryIntent(intent);
				setSharedQueryIntent(intent);
			};
			const onComplete = fg('kd_smart_answers_query_intent_returned_event')
				? onQueryIntentComplete
				: undefined;
			// Both getters fully swallow errors via their internal onError
			// callbacks (onQueryIntentFailed), so no try/catch is needed here.
			if (isIntentDetectionV2OnHelloEnabled || isIntentDetectionV2CustomerTestCohort) {
				await getQueryIntent({
					...intentVariables,
					selectedProduct,
					setQueryIntent: setLocalAndShared,
					onComplete,
					setTransformedQuery,
					setIntentData: setQueryIntentClassificationData,
					setIntentDetectionMetadata: setQueryIntentDetectionMetadata,
					onError: onQueryIntentFailed,
					setRequestId,
					setTraceId,
					metadata,
				});
			} else {
				await getSmartAnswerRoute({
					...intentVariables,
					selectedProduct,
					setQueryIntent: setLocalAndShared,
					onComplete,
					setTransformedQuery,
					onError: onQueryIntentFailed,
					metadata,
				});
			}
			setClassifiedQuery(requestQuery);
			// Stale-request guard: only release the flags if no newer
			// classification has started for a different query.
			if (requestQuery === searchQuery) {
				setLoading(false);
				setIsIntentClassified(true);
			}
		};

		callGetSmartAnswerRoute();
		// selectedProduct excluded: intent detection is keyed to query text only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteARI, intl.locale, searchQuery]);

	return {
		loading,
		transformedQuery,
		queryIntent,
		queryIntentClassificationData,
		queryIntentDetectionMetadata,
		requestId,
		traceId,
		classifiedQuery,
	};
};
